import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { useAuthStore } from '@/core/stores/auth.store';
import { SIGNALR_CONNECTION_STATUS } from './types';
import type { SignalRConnectionStatus } from './types';

interface UseSignalRConnectionOptions {
  hubUrl: string;
  enabled?: boolean;
}

// Backoff between retries of the INITIAL connect attempt — `withAutomaticReconnect()`
// only covers a connection that already reached `Connected` and later drops; it never
// retries a `.start()` call that fails outright. The last delay repeats indefinitely.
const START_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];

interface UseSignalRConnectionResult {
  connectionRef: React.RefObject<HubConnection | null>;
  status: SignalRConnectionStatus;
  /**
   * Increments each time the hub reconnects.
   * Consumers that must re-subscribe after a reconnect should put this in their
   * effect deps instead of calling connection.onreconnected (which cannot be unregistered).
   */
  reconnectCount: number;
}

/**
 * Generic SignalR hub connection hook.
 *
 * Manages the full lifecycle of a HubConnection: connect on mount,
 * reconnect on transient failures, stop on unmount or when disabled.
 * The JWT token is injected via accessTokenFactory so no manual
 * query-string manipulation is needed — the SignalR client handles it.
 *
 * @example
 * const { connectionRef, status } = useSignalRConnection({
 *   hubUrl: `${env.VITE_SIGNALR_HUB_URL}/hubs/notifications?orgId=${orgId}`,
 *   enabled: !!orgId,
 * })
 */
export function useSignalRConnection({
  hubUrl,
  enabled = true,
}: UseSignalRConnectionOptions): UseSignalRConnectionResult {
  const connectionRef = useRef<HubConnection | null>(null);
  const [status, setStatus] = useState<SignalRConnectionStatus>(
    SIGNALR_CONNECTION_STATUS.DISCONNECTED,
  );
  const [reconnectCount, setReconnectCount] = useState(0);

  useEffect(() => {
    if (!enabled || !hubUrl) return;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          const state = useAuthStore.getState();
          return state.tokens?.accessToken ?? '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;
    let cancelled = false;

    connection.onreconnecting(() => {
      setStatus(SIGNALR_CONNECTION_STATUS.RECONNECTING);
    });

    connection.onreconnected(() => {
      setStatus(SIGNALR_CONNECTION_STATUS.CONNECTED);
      setReconnectCount((c) => c + 1);
    });

    connection.onclose(() => {
      setStatus(SIGNALR_CONNECTION_STATUS.DISCONNECTED);
    });

    // `.start()` can be retried on the same (unstarted/disconnected) HubConnection
    // instance — no need to rebuild it. Retries indefinitely with capped backoff so a
    // transient failure during cold boot (reload, direct URL entry) doesn't leave the
    // connection permanently dead for the rest of the session.
    async function startWithRetry() {
      let attempt = 0;
      while (!cancelled) {
        setStatus(
          attempt === 0
            ? SIGNALR_CONNECTION_STATUS.CONNECTING
            : SIGNALR_CONNECTION_STATUS.RECONNECTING,
        );
        try {
          await connection.start();
          if (cancelled) return;
          setStatus(SIGNALR_CONNECTION_STATUS.CONNECTED);
          // A retry succeeding (not the first attempt) means real time passed while
          // disconnected — reconcile the same way an automatic-reconnect-after-drop does.
          if (attempt > 0) setReconnectCount((c) => c + 1);
          return;
        } catch {
          if (cancelled) return;
          setStatus(SIGNALR_CONNECTION_STATUS.DISCONNECTED);
          const delay = START_RETRY_DELAYS_MS[Math.min(attempt, START_RETRY_DELAYS_MS.length - 1)];
          attempt += 1;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    void startWithRetry();

    return () => {
      cancelled = true;
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop();
      }
      connectionRef.current = null;
      setStatus(SIGNALR_CONNECTION_STATUS.DISCONNECTED);
    };
  }, [hubUrl, enabled]);

  return { connectionRef, status, reconnectCount };
}
